/**
 * Flow coordinates for a field (U5a): a geodesic frame that follows every bend of a long, thin outline.
 *
 *   u = d_from / (d_from + d_to)   0 on segment `from`, 1 on segment `to`, monotone along the river
 *   v = d_A / (d_A + d_B)          0 on side A, 1 on side B, ½ on the medial line between them
 *
 * d_X is the geodesic distance inside the outline to the boundary chain X. Side A is the segments strictly between
 * `from` and `to` in anchor order (from + 1 … to − 1), side B the rest (to + 1 … from − 1). A shader reads
 * vUV = (u, v) against uResolution = extent = [area ÷ W, W], W = median(d_A + d_B) — river length × mean width — so
 * stripes bands across the river at angle 0 and lanes along it at 90, both sized in stage units. (PLAN §The field
 * had length = median(d_from + d_to); geodesics hug the inner bank, so that reads 20% short on a tight S, and bands
 * along the centerline come out 24% wide. The bank-to-bank width stays local, and area ÷ width of a constant-width
 * strip is its centerline length at any curvature. median(d_from + d_to) remains the fallback when a side is empty.)
 *
 * Distance (PLAN §Decisions: geodesic, not harmonic): exact Dijkstra on the inside cells over a 16-neighbourhood —
 * orthogonal steps (1), diagonals (√2) and knight moves (√5): a point source's anisotropy is ≤ 2.7% instead of the
 * 8-neighbour chamfer's 8.2%, and measured along rotated strips ≤ 0.3% against 7% (a diagonal river otherwise reads
 * 7% long, and bands crowd and spread around a bend). It costs ≈ 20% more than the 8-neighbour march.
 * There are only three step lengths, so the priority queue is three FIFOs, one per length: settled distances never
 * decrease, so each FIFO receives its entries in order and the least head is the global minimum — exact Dijkstra in
 * O(cells), no heap. Seeds are not just the chain's boundary cells: every inside cell whose nearest edge (the EDT's)
 * lies on the chain starts at its exact Euclidean distance (the nearest boundary point is always visible, so that is
 * its geodesic distance), plus the cells within a texel of the chain's edges. Dijkstra only carries the field past
 * the chain's Voronoi region, and the endpoints are exact.
 *
 * Moves never cross outside cells: a diagonal needs one of its two orthogonal cells inside, a knight move both cells
 * its line passes through. PAD_TEXELS ≥ 2 keeps every inside cell two cells from the grid edge, so offsets of ±2
 * never wrap a row.
 */
import { EDGE_STRIDE, edgeDist2 } from './raster'

/** Module-local binding for the hot loop (see index.js: vitest's module runner makes imports a getter per call). */
const segDist2 = edgeDist2

/** Chain labels per edge / cell. 0 = none (an edge of a hole subpath). */
const FROM = 1
const TO = 2
const SIDE_A = 3
const SIDE_B = 4

/** Step lengths in texels, one FIFO each. */
const STEPS = [1, Math.SQRT2, Math.sqrt(5)]
/** Seed order key resolution, 1/1024 texel: seeds are popped in this order, at their exact distances. */
const KEY_SCALE = 1024

/** Ring-buffer FIFO of (cell, distance), grown by doubling. */
class Fifo {
	constructor(cap = 1024) {
		this.cell = new Int32Array(cap)
		this.dist = new Float64Array(cap)
		this.mask = cap - 1
		this.head = 0
		this.size = 0
	}
	push(c, d) {
		if (this.size > this.mask) this.grow()
		const i = (this.head + this.size) & this.mask
		this.cell[i] = c
		this.dist[i] = d
		this.size++
	}
	grow() {
		const cap = (this.mask + 1) * 2
		const cell = new Int32Array(cap)
		const dist = new Float64Array(cap)
		for (let i = 0; i < this.size; i++) {
			const j = (this.head + i) & this.mask
			cell[i] = this.cell[j]
			dist[i] = this.dist[j]
		}
		this.cell = cell
		this.dist = dist
		this.mask = cap - 1
		this.head = 0
	}
}

/** Flow's own pooled buffers, grown to the largest grid seen (the rest is borrowed from the baker's pool). */
let pool = null
function flowScratch(n) {
	if (!pool) pool = { queues: STEPS.map(() => new Fifo()), dist: null, keys: null }
	if (!pool.dist || pool.dist.length < n) {
		pool.dist = new Float64Array(n)
		pool.keys = new Float64Array(n)
	}
	return pool
}

/** Knight moves on (16-neighbourhood) or off (8-neighbour chamfer). */
const KNIGHT = true

/**
 * Geodesic distance (texels) from chain `target` to every inside cell, into `dist`; outside and unreached cells read
 * Infinity. Returns false when the chain has no seed (nothing of it borders an inside cell).
 *
 * Every seed holds its exact Euclidean distance to the chain, a lower bound no path beats, so seeds are settled from
 * the start; only the frontier ones (an open cell within reach) enter the seed queue. The region a chain is nearest
 * to costs one scan, not a Dijkstra.
 */
function march(target, ctx, dist) {
	const { n, width, height, texel, ox, oy, data, inside, cellLabel, edges, edgeLabel, state, list, keys, queues } =
		ctx
	// state: 0 outside, 1 open, 2 settled
	for (let c = 0; c < n; c++) state[c] = inside[c]
	dist.fill(Infinity, 0, n)
	let k = 0
	// Seeds 1: the chain's Voronoi region — r is the exact distance to the nearest edge, which is on the chain.
	for (let c = 0; c < n; c++) {
		if (cellLabel[c] === target) {
			dist[c] = data[c * 4] / texel
			state[c] = 2
			list[k++] = c
		}
	}
	// Seeds 2: inside cells within a texel of a chain edge whose nearest edge is a neighbour's (corners, short segments).
	const step = 0.5 * texel
	const edgeCount = edges.length / EDGE_STRIDE
	for (let e = 0; e < edgeCount; e++) {
		if (edgeLabel[e] !== target) continue
		const o = e * EDGE_STRIDE
		const ax = edges[o]
		const ay = edges[o + 1]
		const dx = edges[o + 2]
		const dy = edges[o + 3]
		const samples = Math.max(1, Math.ceil(Math.sqrt(dx * dx + dy * dy) / step))
		let last = -1
		for (let s = 0; s <= samples; s++) {
			const t = s / samples
			const cx = Math.floor((ax + t * dx - ox) / texel)
			const cy = Math.floor((ay + t * dy - oy) / texel)
			const cc = cy * width + cx
			if (cc === last) continue
			last = cc
			for (let y = Math.max(0, cy - 1); y <= Math.min(height - 1, cy + 1); y++) {
				for (let x = Math.max(0, cx - 1); x <= Math.min(width - 1, cx + 1); x++) {
					const c = y * width + x
					if (!inside[c]) continue
					const d = Math.sqrt(segDist2(edges, e, ox + (x + 0.5) * texel, oy + (y + 0.5) * texel)) / texel
					if (d < dist[c]) {
						if (state[c] !== 2) {
							state[c] = 2
							list[k++] = c
						}
						dist[c] = d
					}
				}
			}
		}
	}
	if (!k) return false
	// Frontier only, in packed (quantized distance, cell) order: one native numeric sort.
	const w = width
	const w2 = 2 * width
	let f = 0
	for (let i = 0; i < k; i++) {
		const c = list[i]
		if (
			state[c + 1] === 1 ||
			state[c - 1] === 1 ||
			state[c + w] === 1 ||
			state[c - w] === 1 ||
			state[c + w + 1] === 1 ||
			state[c + w - 1] === 1 ||
			state[c - w + 1] === 1 ||
			state[c - w - 1] === 1 ||
			(KNIGHT &&
				(state[c + w + 2] === 1 ||
					state[c + w - 2] === 1 ||
					state[c - w + 2] === 1 ||
					state[c - w - 2] === 1 ||
					state[c + w2 + 1] === 1 ||
					state[c + w2 - 1] === 1 ||
					state[c - w2 + 1] === 1 ||
					state[c - w2 - 1] === 1))
		)
			keys[f++] = Math.round(dist[c] * KEY_SCALE) * n + c
	}
	const sorted = keys.subarray(0, f).sort()
	for (let i = 0; i < f; i++) list[i] = sorted[i] % n

	const [q0, q1, q2] = queues
	for (const q of queues) q.head = q.size = 0
	const S1 = STEPS[1]
	const S2 = STEPS[2]
	let sHead = 0
	for (;;) {
		// Drop settled heads, then take the least of the four.
		while (q0.size && state[q0.cell[q0.head]] === 2) {
			q0.head = (q0.head + 1) & q0.mask
			q0.size--
		}
		while (q1.size && state[q1.cell[q1.head]] === 2) {
			q1.head = (q1.head + 1) & q1.mask
			q1.size--
		}
		while (q2.size && state[q2.cell[q2.head]] === 2) {
			q2.head = (q2.head + 1) & q2.mask
			q2.size--
		}
		let c = -1
		let dc = Infinity
		let src = null
		if (sHead < f) {
			c = list[sHead]
			dc = dist[c]
		}
		if (q0.size && q0.dist[q0.head] < dc) {
			src = q0
			dc = q0.dist[q0.head]
		}
		if (q1.size && q1.dist[q1.head] < dc) {
			src = q1
			dc = q1.dist[q1.head]
		}
		if (q2.size && q2.dist[q2.head] < dc) {
			src = q2
			dc = q2.dist[q2.head]
		}
		if (src) {
			c = src.cell[src.head]
			src.head = (src.head + 1) & src.mask
			src.size--
			state[c] = 2
		} else if (c >= 0) sHead++
		else break
		// Orthogonal, step 1.
		let nd = dc + 1
		let m = c + 1
		if (state[m] === 1 && nd < dist[m]) {
			dist[m] = nd
			q0.push(m, nd)
		}
		m = c - 1
		if (state[m] === 1 && nd < dist[m]) {
			dist[m] = nd
			q0.push(m, nd)
		}
		m = c + w
		if (state[m] === 1 && nd < dist[m]) {
			dist[m] = nd
			q0.push(m, nd)
		}
		m = c - w
		if (state[m] === 1 && nd < dist[m]) {
			dist[m] = nd
			q0.push(m, nd)
		}
		// Diagonal, step √2: one of the two orthogonal cells it passes between must be inside.
		const r = state[c + 1] !== 0
		const l = state[c - 1] !== 0
		const dn = state[c + w] !== 0
		const up = state[c - w] !== 0
		nd = dc + S1
		m = c + w + 1
		if (state[m] === 1 && (r || dn) && nd < dist[m]) {
			dist[m] = nd
			q1.push(m, nd)
		}
		m = c + w - 1
		if (state[m] === 1 && (l || dn) && nd < dist[m]) {
			dist[m] = nd
			q1.push(m, nd)
		}
		m = c - w + 1
		if (state[m] === 1 && (r || up) && nd < dist[m]) {
			dist[m] = nd
			q1.push(m, nd)
		}
		m = c - w - 1
		if (state[m] === 1 && (l || up) && nd < dist[m]) {
			dist[m] = nd
			q1.push(m, nd)
		}
		if (!KNIGHT) continue
		// Knight, step √5: both cells its line crosses must be inside — (2, 1) crosses (1, 0) and (1, 1).
		nd = dc + S2
		const dr = state[c + w + 1] !== 0
		const dl = state[c + w - 1] !== 0
		const ur = state[c - w + 1] !== 0
		const ul = state[c - w - 1] !== 0
		m = c + w + 2
		if (state[m] === 1 && r && dr && nd < dist[m]) {
			dist[m] = nd
			q2.push(m, nd)
		}
		m = c - w + 2
		if (state[m] === 1 && r && ur && nd < dist[m]) {
			dist[m] = nd
			q2.push(m, nd)
		}
		m = c + w - 2
		if (state[m] === 1 && l && dl && nd < dist[m]) {
			dist[m] = nd
			q2.push(m, nd)
		}
		m = c - w - 2
		if (state[m] === 1 && l && ul && nd < dist[m]) {
			dist[m] = nd
			q2.push(m, nd)
		}
		m = c + w2 + 1
		if (state[m] === 1 && dn && dr && nd < dist[m]) {
			dist[m] = nd
			q2.push(m, nd)
		}
		m = c + w2 - 1
		if (state[m] === 1 && dn && dl && nd < dist[m]) {
			dist[m] = nd
			q2.push(m, nd)
		}
		m = c - w2 + 1
		if (state[m] === 1 && up && ur && nd < dist[m]) {
			dist[m] = nd
			q2.push(m, nd)
		}
		m = c - w2 - 1
		if (state[m] === 1 && up && ul && nd < dist[m]) {
			dist[m] = nd
			q2.push(m, nd)
		}
	}
	return true
}

/** a / (a + b) over two distances; a component one chain cannot reach reads 0 (near the other's) or 1. */
const ratio = (a, b) => {
	const s = a + b
	if (s < Infinity) return s > 0 ? a / s : 0.5
	return a < Infinity ? 0 : b < Infinity ? 1 : 0
}

/** k-th smallest of arr[0 … len) in place (Hoare quickselect, middle pivot). */
function select(arr, len, k) {
	let lo = 0
	let hi = len - 1
	while (lo < hi) {
		const pivot = arr[(lo + hi) >> 1]
		let i = lo
		let j = hi
		while (i <= j) {
			while (arr[i] < pivot) i++
			while (arr[j] > pivot) j--
			if (i <= j) {
				const t = arr[i]
				arr[i] = arr[j]
				arr[j] = t
				i++
				j--
			}
		}
		if (k <= j) hi = j
		else if (k >= i) lo = i
		else break
	}
	return arr[k]
}

/** Segment index wrapped into [0, count), as `segmentChord` wraps it; NaN when not a finite number. */
const wrap = (i, count) => (Number.isFinite(i) ? ((Math.trunc(i) % count) + count) % count : Number.NaN)

/**
 * `from` / `to` resolved against the segment count, or null when they cannot carry a flow: not finite, fewer than
 * four segments, the same segment, or adjacent ones (no side between them). U5c refuses these in the UI.
 * @returns {{ from: number, to: number } | null}
 */
export function flowEnds(flow, segments) {
	if (!flow || !(segments >= 4)) return null
	const from = wrap(flow.from, segments)
	const to = wrap(flow.to, segments)
	if (Number.isNaN(from) || Number.isNaN(to)) return null
	const gap = (to - from + segments) % segments
	if (gap === 0 || gap === 1 || gap === segments - 1) return null
	return { from, to }
}

/**
 * Writes u into g and v into b of every inside cell of a baked field, and returns the flow extent.
 * Must run before `dilateChannels` (it reads `edge`, which the dilation overwrites).
 *
 * @param {Float32Array} data the field, r filled (inside positive, stage units)
 * @param {Uint8Array} inside 1 inside, per cell
 * @param {{ width: number, height: number, origin: [number, number], texel: number }} grid
 * @param {Float64Array} edges `edgesOf` output
 * @param {{ segment: Int32Array, segments: number }} info flattenPath's edge → segment map
 * @param {{ from: number, to: number }} flow segment indices (wrapped here)
 * @param {{ edge: Int32Array, dist2: Float64Array, flag: Uint8Array, seed: Int32Array, nearest: Int32Array }} buf
 *   the baker's pooled buffers: `edge` read (nearest flattened edge per cell); the rest are free at this point and
 *   used as scratch
 * @returns {[number, number] | null} [area ÷ W, W], W = median(d_A + d_B), in stage units (area by SDF coverage);
 *   null (and g/b untouched) when the endpoints cannot carry a flow (see flowEnds) or an end chain borders no
 *   inside cell
 */
export function flowChannels(data, inside, grid, edges, info, flow, buf) {
	const ends = flowEnds(flow, info?.segments)
	if (!ends) return null
	const { width, height, texel } = grid
	const n = width * height
	const segments = info.segments
	const { from, to } = ends
	const span = (to - from + segments) % segments
	const labelOf = s => {
		if (s < 0) return 0
		if (s === from) return FROM
		if (s === to) return TO
		return (s - from + segments) % segments < span ? SIDE_A : SIDE_B
	}
	const segLabel = new Uint8Array(segments)
	for (let s = 0; s < segments; s++) segLabel[s] = labelOf(s)
	const edgeLabel = new Uint8Array(info.segment.length)
	for (let e = 0; e < edgeLabel.length; e++) edgeLabel[e] = info.segment[e] < 0 ? 0 : segLabel[info.segment[e]]

	const { edge, dist2, flag, seed, nearest } = buf
	const cellLabel = nearest // borrowed: per inside cell, the label of its nearest edge; 0 outside
	let count = 0
	for (let c = 0; c < n; c++) {
		if (inside[c]) {
			cellLabel[c] = edgeLabel[edge[c]]
			count++
		} else cellLabel[c] = 0
	}
	if (!count) return null
	const own = flowScratch(n)
	const ctx = {
		n,
		width,
		height,
		texel,
		ox: grid.origin[0],
		oy: grid.origin[1],
		data,
		inside,
		cellLabel,
		edges,
		edgeLabel,
		state: flag,
		list: seed,
		keys: own.keys,
		queues: own.queues,
	}
	const d1 = dist2 // borrowed: r is already packed into data
	const d2 = own.dist
	if (!march(FROM, ctx, d1) || !march(TO, ctx, d2)) return null

	// u, and the per-cell end-to-end sums compacted into d1's head (index k ≤ c: already read) for the median.
	let k = 0
	for (let c = 0; c < n; c++) {
		if (!inside[c]) continue
		data[c * 4 + 1] = ratio(d1[c], d2[c])
		const sum = d1[c] + d2[c]
		if (sum < Infinity) d1[k++] = sum
	}
	if (!k) return null
	const endToEnd = select(d1, k, k >> 1) * texel

	// v across the river. Both sides exist (endpoints are not adjacent); a side bordering no inside cell → v = ½.
	const hasA = march(SIDE_A, ctx, d1)
	const hasB = hasA && march(SIDE_B, ctx, d2)
	k = 0
	for (let c = 0; c < n; c++) {
		if (!inside[c]) continue
		if (!hasB) {
			data[c * 4 + 2] = 0.5
			continue
		}
		data[c * 4 + 2] = ratio(d1[c], d2[c])
		const sum = d1[c] + d2[c]
		if (sum < Infinity) d1[k++] = sum
	}
	// Area by SDF coverage (a cell counts its clamped 0.5 + r / texel): exact for straight edges at any texel.
	let cover = 0
	for (let c = 0; c < n; c++) {
		const f = 0.5 + data[c * 4] / texel
		cover += f <= 0 ? 0 : f >= 1 ? 1 : f
	}
	const area = cover * texel * texel
	// Mean width = median bank-to-bank distance, length = area ÷ width: for a strip of constant width that is the
	// centerline length at any curvature. median(d_from + d_to) is the fallback (no sides): geodesics hug the inner
	// bank, so it reads short in bends — 505 for a 628 centerline on the test S.
	const across = hasB && k ? select(d1, k, k >> 1) * texel : 0
	return across > 0 ? [area / across, across] : [endToEnd, area / endToEnd]
}
